/**
 * iOS-Safari-safe blob download helper.
 *
 * Problem: Programmatic `a.click()` on a blob URL is blocked by iOS Safari
 * (and by the WKWebView used inside the Emergent preview app). Chrome/Firefox
 * on desktop work fine with the classic anchor pattern, but iOS needs a
 * different route — opening the blob URL in a new tab so the user can tap
 * the Share icon → "Save to Files".
 *
 * Usage:
 *   import { downloadBlob } from '@/utils/downloadBlob';
 *   downloadBlob(blob, 'my-ebook.pdf');
 */

export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // Classic iOS devices
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ identifies as MacIntel with touch points
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  return false;
}

export function isSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
}

/**
 * Download a blob (or fetch a URL) as a file.
 * Handles iOS Safari + desktop browsers correctly.
 *
 * @param {Blob|string} blobOrUrl  A Blob, or an existing object URL string.
 * @param {string} filename        Suggested filename (used on desktop; iOS
 *                                 ignores it and uses the server's
 *                                 Content-Disposition or lets the user pick).
 * @returns {Promise<{success: boolean, ios: boolean, message?: string}>}
 */
export async function downloadBlob(blobOrUrl, filename = 'download') {
  const url = typeof blobOrUrl === 'string'
    ? blobOrUrl
    : URL.createObjectURL(blobOrUrl);

  const createdHere = typeof blobOrUrl !== 'string';
  const ios = isIOS();

  try {
    if (ios) {
      // iOS path (spec 2026-04-30):
      //   1. If navigator.share is available, hand the URL to the native
      //      Share sheet — the user picks "Save Video" / "Save to Files"
      //      / "Save to Photos" themselves.
      //   2. Otherwise navigate same-tab. Safari renders the file inline
      //      and the user taps Share → Save manually.
      // We deliberately do NOT use window.open() — it's popup-blocked on
      // iOS Safari and unreliable inside WKWebView shells.
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        try {
          await navigator.share({
            title: typeof filename === 'string' ? filename : 'Your File',
            url,
          });
          return {
            success: true,
            ios: true,
            message: 'Picked an action from the Share sheet.',
          };
        } catch (err) {
          if (err && err.name === 'AbortError') {
            return { success: true, ios: true, message: 'Share cancelled.' };
          }
          // fall through to same-tab navigation
        }
      }
      window.location.href = url;
      return {
        success: true,
        ios: true,
        message: "Tap the Share icon then 'Save Video' / 'Save to Files'.",
      };
    }

    // Desktop / Android: classic anchor + programmatic click. NEVER open()
    // IMPORTANT: anchor MUST be appended to the DOM — Chrome/Firefox work
    // without it, but some Chromium-based WKWebview shells require it.
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      try { document.body.removeChild(a); } catch { /* ignore */ }
    }, 100);

    return { success: true, ios: false };
  } finally {
    // Revoke after a delay so the browser has time to start the download
    if (createdHere) {
      setTimeout(() => {
        try { URL.revokeObjectURL(url); } catch { /* ignore */ }
      }, 10_000);
    }
  }
}

/**
 * Convenience: download an authenticated API response as a blob file.
 * Handles iOS Safari correctly.
 *
 * @param {import('axios').AxiosInstance} api   Configured axios instance
 * @param {string} path                         Relative API path (with or without /api prefix)
 * @param {string} filename                     Target filename
 * @param {object} options                      { method?: 'GET'|'POST', data?, mime? }
 * @returns {Promise<{success:boolean, ios:boolean, message?:string}>}
 */
export async function downloadFromApi(api, path, filename, options = {}) {
  const method = (options.method || 'GET').toLowerCase();
  const mime = options.mime || 'application/octet-stream';

  const config = { responseType: 'blob', timeout: options.timeout || 180_000 };
  const response = method === 'post'
    ? await api.post(path, options.data || {}, config)
    : await api.get(path, config);

  const blob = new Blob([response.data], { type: mime });
  return downloadBlob(blob, filename);
}
